import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useSiteStatus } from '@newfold/huapi-js';
import type { HostingSiteAddV2BodyDatacenter } from '@newfold/huapi-js/src/index.schemas';

import { useMFEContext } from '~/components/MFEProvider';
import WPPendingDialog from '~/components/SSOLink/WPPendingDialog';
import { useBrandInfo } from '~/hooks/useBrandInfo';
import useFeature from '~/hooks/useFeature';
import useFilteredHostingSites from '~/hooks/useFilteredHostingSites';
import useHostingInfo from '~/hooks/useHostingInfo';
import useJarvisDomainLogic from '~/hooks/useJarvisDomainLogic';

import CloudAddSite from '../Cloud/scenes/AddSite';
import CloudAddSiteWithCTB from '../Cloud/scenes/AddSite/CloudAddSiteWithCTB';
import TransferOptions from './components/TransferOptions';
import { useAddSiteContext } from './context/AddSiteContext';
import AddSite from './scenes/AddSite';
import AddSiteWithCTB from './scenes/AddSiteWithCTB';
import SetupSite from './scenes/SetupSite';

export type AddSiteFormDataTypes = {
  multisite_type?: string;
  siteName?: string;
  domain?: string;
  path?: string;
  subtype?: string;
  autoGeneration?: boolean;
  migrationId?: string;
  isMigrating?: boolean;
  siteUrl?: string;
  title?: string;
  action?: string;
  admin_email?: string;
  admin_user?: string;
  datacenter?: HostingSiteAddV2BodyDatacenter;
  disk_limit_gib?: number;
  workers?: number;
  clone_site_id?: number;
  migration_id?: string;
  enable_multisite?: boolean;
};

const AddSetupSiteFlows = () => {
  const { t } = useTranslation(['addSites', 'domains', 'errors']);
  const location = useLocation();
  const { siteId } = useParams();
  // @ts-expect-error
  const { hostingId } = useMFEContext();
  const navigate = useNavigate();
  const [isDomainWithCTB] = useJarvisDomainLogic();
  const { brandFromJWT } = useBrandInfo();

  const isBHorHG =
    brandFromJWT.toLowerCase() === 'bluehost' ||
    brandFromJWT.toLowerCase() === 'hostgator';
  const purchaseSiteMigrator = location.pathname.endsWith('transfer');
  const [open, setOpen] = useState(false);
  const { chosenSiteId, setChosenSiteId, changeLoading, setChangeLoading } =
    useAddSiteContext();

  useEffect(() => {
    if (siteId) {
      setChosenSiteId(Number(siteId));
    }
  }, [siteId, setChosenSiteId]);

  // comfortLevel will get set in mole survey component
  const [comfortLevel, setComfortLevel] = useState<number | null>(null);

  const [isHostingCloud, , isClientReady] = useFeature('atomic');

  const { data: siteStatusInfo, refetch: refetchSiteStatus } = useSiteStatus(
    Number(chosenSiteId),
    {
      query: {
        enabled: false,
      },
    },
  );
  const siteStatus = siteStatusInfo?.data;
  const getInterstitialTitle = () => {
    const hasSiteStatus = Boolean(
      siteStatus?.cloudflare_ready === false || siteStatus?.pending_url,
    );
    if (hasSiteStatus) return undefined;
    return siteId ? t('siteUpdated') : t('siteCreated');
  };

  const handleSso = async () => {
    await refetchSiteStatus();
    setChangeLoading(false);
    setOpen(true);
  };

  const { data: sitesInfo } = useFilteredHostingSites(hostingId);

  const { hostingAccount } = useHostingInfo();
  const hostingPlatform = hostingAccount?.data?.platform;

  const siteList = sitesInfo?.data?.items;
  const accountProdInstaId = hostingAccount?.data?.id || '';
  // (determining type -- "loading...")
  if (!isClientReady) return null;

  // Cloud Hosting ("Atomic")
  if (isHostingCloud)
    return isDomainWithCTB && isBHorHG ? (
      <CloudAddSiteWithCTB
        siteList={siteList}
        accountProdInstaId={accountProdInstaId}
      />
    ) : (
      <CloudAddSite siteList={siteList} />
    );

  // Shared Hosting
  return (
    <>
      {siteId ? (
        <SetupSite
          redirectSSO={handleSso}
          changeLoading={changeLoading}
          setChangeLoading={setChangeLoading}
          comfortLevel={comfortLevel}
          setComfortLevel={setComfortLevel}
        />
      ) : isDomainWithCTB && hostingPlatform === 'jarvis' ? (
        <AddSiteWithCTB
          siteList={siteList}
          redirectSSO={handleSso}
          setChosenSiteId={setChosenSiteId}
          chosenSiteId={chosenSiteId}
          changeLoading={changeLoading}
          setChangeLoading={setChangeLoading}
        />
      ) : (
        <AddSite
          siteList={siteList}
          redirectSSO={handleSso}
          setChosenSiteId={setChosenSiteId}
          chosenSiteId={chosenSiteId}
          changeLoading={changeLoading}
          setChangeLoading={setChangeLoading}
        />
      )}
      <WPPendingDialog
        open={open}
        siteId={chosenSiteId}
        ssoQueryParams={
          comfortLevel ? `&survey_comfort_level=${comfortLevel}` : ''
        }
        onAction={() => {
          setOpen(false);
          navigate(chosenSiteId ? `/sites/${chosenSiteId}` : '/');
        }}
        onClose={() => {
          setOpen(false);
          navigate(chosenSiteId ? `/sites/${chosenSiteId}` : '/');
        }}
        title={getInterstitialTitle()}
        siteStatus={siteStatus}
      />
      {purchaseSiteMigrator && <TransferOptions />}
    </>
  );
};

export default AddSetupSiteFlows;
